import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  message,
  Modal,
  Input,
} from 'antd';
import PropTypes from 'prop-types';
import {
  DoubleRightOutlined,
  RightOutlined,
} from '@ant-design/icons';

import API from 'utils/api';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import NumericInput from 'components/NumericInput';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';

const baseURI = 'configuracion/segmentos-de-folios/';

const SegmentosFoliador = ({
  isMutable,
  setSegmentos,
  idConfiguracion,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [data, setData] = useState([]);
  const [tiposCaracteres, setTiposCaracteres] = useState([]);
  const [valores, setValores] = useState([]);
  const [filterdValores, setFilterdValores] = useState([]);
  const [consecutivos, setConsecutivos] = useState([]);
  const [isConsecutivo, setIsConsecutivo] = useState(false);
  const [isFijo, setIsFijo] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      if (idConfiguracion) {
        const res = await API.get(baseURI, {
          params: {
            configuracion_de_folio: idConfiguracion,
          },
        });
        if (res?.status === 200) {
          let dataWithKeys = res.data.map((item) => ({
            ...item,
            modificable: false,
            deletable: false,
            key: item.id,
          }));
          dataWithKeys = dataWithKeys
            .sort((a, b) => a.posicion.toString().localeCompare(b.posicion));
          setSegmentos(dataWithKeys);
          setData(dataWithKeys);
        } else {
          const segmentoDefault = [{
            tipo_de_segmento: 2,
            longitud_del_folio: 4,
            folio_siguiente: 1,
            id: 1,
            posicion: 1,
            modificable: true,
            deletable: false,
          }];
          setSegmentos(segmentoDefault);
          setData(segmentoDefault);
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const fetchAll = async () => {
    try {
      setLoading(true);
      const resTiposCaracteres = await API.get('configuracion/tipos-de-caracteres/');
      setTiposCaracteres(resTiposCaracteres.data);
      const resConsecutivos = await API.get('recaudacion/periodos/');
      setConsecutivos(resConsecutivos.data);
      const resValores = await API.get('configuracion/catalogos-de-valor/');
      setValores(resValores.data);
      await fetchData();
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  useEffect(() => {
    fetchAll();
    return () => API.tryCancel;
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [idConfiguracion]);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setIsConsecutivo(false);
    setFilterdValores([]);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const clone = [...data];
      if (!idConfiguracion) {
        if (!selectedRowKeys.length) {
          clone.push({
            ...values,
            posicion: clone.length + 1,
            id: clone.length + 1,
            modificable: true,
            deletable: true,
          });
          setData(clone);
          setSegmentos(clone);
          onCancel();
        } else {
          const [key] = selectedRowKeys;
          const res = clone.map((item) => (item.id === key
            ? { ...values, posicion: item.posicion, key } : item));
          setData(res);
          setSegmentos(res);
          onCancel();
        }
      } else {
        const res = await API.post(baseURI, {
          ...values,
          posicion: data.length + 1,
          configuracion_de_folio: idConfiguracion,
        });
        if (res && res.status === 201) {
          onSuccess(res, 'Agregado correctamente');
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = () => {
    const [key] = selectedRowKeys;
    const clone = data.filter((item) => item.id !== key);
    const normalized = clone.map((item, idx) => ({ ...item, id: idx }));
    setData(normalized);
    setSegmentos(normalized);
    onCancel();
    setVisibleAlert(false);
  };

  const handleOnRowClick = (record) => {
    if (!record.modificable) {
      setSelectedRowKeys([record.key]);
      form.setFieldsValue(record);
    }
  };

  const rowSelection = {
    type: 'radio',
    selectedRowKeys,
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickEdit = () => {
    const [key] = selectedRowKeys;
    const match = data.find((e) => e.id === key);
    if (match?.modificable) {
      setVisible(true);
    } else {
      message.info('No se pueden editar los segmentos previos');
    }
  };

  const onClickDelete = () => {
    const [key] = selectedRowKeys;
    const match = data.find((e) => e.id === key);
    if (match && match.deletable) {
      setVisibleAlert(true);
    } else {
      message.info('No se pueden eliminar los segmentos previos');
    }
  };

  const rules = {
    required: [
      {
        required: true,
        message: 'El campo es requerido',
      },
    ],
  };

  const getTipoCaracter = (record, returnId = false) => {
    const valor = valores.find((v) => v.id === record.catalogo_de_valor);
    let id = valor ? valor.tipado : 2;
    id = record.fijo ? 1 : id;
    if (returnId) {
      return id;
    }
    const tipo = tiposCaracteres.find((t) => t.id === id);
    return tipo ? tipo.descripcion : '';
  };

  const columns = [
    {
      titleText: 'Posición',
      dataIndex: 'posicion',
      key: 'posicion',
      width: 100,
    },
    {
      titleText: 'Tipo de caracter',
      dataIndex: 'tipo_de_segmento',
      key: 'tipo_de_segmento',
      width: 150,
      render: (val) => tiposCaracteres.find((e) => e.id === val)?.descripcion,
    },
    {
      titleText: 'Longitud',
      dataIndex: 'longitud_del_folio',
      key: 'longitud_del_folio',
      width: 100,
    },
    {
      titleText: 'Valor fijo/siguiente',
      dataIndex: 'catalogo_de_valor',
      key: 'catalogo_de_valor',
      width: 180,
      render: (val, record) => {
        let output;
        let valor;
        const tipo = getTipoCaracter(record, true);
        switch (tipo) {
          case 1:
            output = record.fijo;
            break;
          case 2:
            output = record.folio_siguiente || '';
            break;
          case 3:
            valor = valores.find((t) => t.id === val);
            output = valor ? valor.clave : '';
            break;
          default:
            break;
        }
        return output || '';
      },
    },
  ];

  const moveToLocal = (where) => {
    const [key] = selectedRowKeys;
    const match = data.find((item) => item.id === key);
    const filtered = data.filter((item) => item.id !== key);
    switch (where) {
      case 'first':
        filtered.splice(0, 0, match);
        break;
      case 'up':
        filtered.splice(match.posicion - 2, 0, match);
        break;
      case 'down':
        filtered.splice(match.posicion, 0, match);
        break;
      case 'last':
        filtered.splice(data.length, 0, match);
        break;
      default:
        break;
    }
    const final = filtered.map((i, idx) => ({ ...i, posicion: idx + 1 }));
    setData(final);
    setSegmentos(final);
  };

  const moveTo = async (where) => {
    try {
      setLoading(true);
      if (idConfiguracion) {
        const [key] = selectedRowKeys;
        const match = data.find((item) => item.id === key);
        let posicion = 0;
        switch (where) {
          case 'first':
            posicion = 1;
            break;
          case 'up':
            posicion = match.posicion - 1;
            break;
          case 'down':
            posicion = match.posicion + 1;
            break;
          case 'last':
            posicion = data.length;
            break;
          default:
            posicion = match.posicion;
            break;
        }
        const res = await API.patch(`${baseURI}${key}/`, {
          posicion,
        });
        if (res?.status === 200) {
          await fetchData();
        }
      } else {
        moveToLocal(where);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const sortTypes = (arr = tiposCaracteres) => {
    const sorted = arr.map((t) => ({
      ...t,
      disabled: !!data.find((i) => i.tipo_de_segmento_contable === t.id
        && t.descripcion.toLowerCase() !== 'control interno'),
    }));
    setTiposCaracteres(sorted.sort((a, b) => a.disabled.toString().localeCompare(b.disabled)));
  };

  useEffect(() => {
    sortTypes();
    // eslint-disable-next-line
  }, [data]);

  const restProps = isMutable ? {
    rowSelection,
    handleOnRowClick,
  } : {};

  const SortControls = () => (
    <Col className="sorting">
      <Button
        type="link"
        icon={<DoubleRightOutlined />}
        disabled={!selectedRowKeys.length
          || data.find((e) => e.key === selectedRowKeys[0]).posicion <= 2}
        onClick={() => moveTo('first')}
      />
      <Button
        type="link"
        icon={<RightOutlined />}
        disabled={!selectedRowKeys.length
          || data.find((e) => e.key === selectedRowKeys[0]).posicion < 2}
        onClick={() => moveTo('up')}
      />
      <Button
        type="link"
        icon={<RightOutlined />}
        disabled={!selectedRowKeys.length
          || data.find((e) => e.key === selectedRowKeys[0]).posicion === data.length}
        onClick={() => moveTo('down')}
      />
      <Button
        type="link"
        icon={<DoubleRightOutlined />}
        disabled={!selectedRowKeys.length
          || data.find((e) => e.key === selectedRowKeys[0]).posicion >= data.length - 1}
        onClick={() => moveTo('last')}
      />
    </Col>
  );

  const onChangeValor = (val) => {
    if (val === 1 || val === 2 || val === 4) {
      form.setFieldsValue({ longitud_del_folio: 2 });
    } else if (val === 3) {
      form.setFieldsValue({ longitud_del_folio: 3 });
    } else if (val === 5) {
      form.setFieldsValue({ longitud_del_folio: 4 });
    }
  };

  return (
    <Row id="presupuestos-segmentos" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Row>
          <Col style={{ width: `calc(100% - ${isMutable ? 50 : 0}px)` }}>
            <Table
              cols={columns}
              data={data}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...restProps}
              controls={{
                onClickAdd,
                onClickEdit,
                onClickDelete,
              }}
              allowSearch={false}
              allowSort={false}
              allowAdd={isMutable}
              allowEdit={!idConfiguracion}
              allowDelete={!idConfiguracion}
            />
          </Col>
          {isMutable && <SortControls />}
        </Row>
        <Modal
          open={visible}
          title={(
            <FormSubmitControls
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Segmento`}
            />
          )}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          onCancel={onCancel}
          footer={null}
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            onValuesChange={(changedValues, allValues) => {
              const { fijo, catalogo_de_valor } = changedValues;
              if (fijo) {
                form.setFieldsValue({ longitud_del_folio: fijo.length });
              } else if (catalogo_de_valor && allValues.tipo_de_segmento !== 3) {
                const valor = valores.find((v) => v.id === catalogo_de_valor);
                form.setFieldsValue({ longitud_del_folio: valor.clave.length });
              } else if (fijo === '') {
                form.setFieldsValue({ longitud_del_folio: 0 });
              }
            }}
          >
            <Col span={24}>
              <Form.Item
                name="tipo_de_segmento"
                label="Tipo de caracter"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  dataSource={tiposCaracteres}
                  onChange={async (val) => {
                    if (val) {
                      setIsConsecutivo(val === 1);
                      setIsFijo(val === 3);
                      setFilterdValores(valores);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <NumericInput
                disabled={form.getFieldValue('tipo_de_segmento') === 1
                  || form.getFieldValue('tipo_de_segmento') === 3
                  || form.getFieldValue('tipo_de_segmento') === 2}
                label="Longitud"
                name="longitud_del_folio"
                required
              />
            </Col>
            {(!isConsecutivo && !isFijo && form.getFieldValue('tipo_de_segmento')) && (
              <Col span={24}>
                <Form.Item
                  label="Valor"
                  name="catalogo_de_valor"
                  hasFeedback
                  rules={rules.required}
                >
                  <Select
                    dataSource={filterdValores}
                    keyLabelRender
                    onChange={form.getFieldValue('tipo_de_segmento') === 3
                      ? onChangeValor : null}
                  />
                </Form.Item>
              </Col>
            )}
            {isConsecutivo && (
              <Col span={24}>
                <NumericInput
                  label="Valor inicial"
                  name="folio_siguiente"
                  required
                  onChange={({ target: { value } }) => {
                    if (value && form.getFieldValue('tipo_de_segmento') === 1) {
                      form.setFieldsValue({ longitud_del_folio: value.length });
                    }
                  }}
                />
              </Col>
            )}
            {isFijo && (
              <Col span={24}>
                <Form.Item
                  label="Valor fijo"
                  name="fijo"
                  rules={rules.required}
                  hasFeedback
                >
                  <Input maxLength={10} className="uppercase" />
                </Form.Item>
              </Col>
            )}
            {isConsecutivo && (
              <Col span={24}>
                <Form.Item
                  name="catalogo_de_periodo"
                  rules={rules.required}
                  label="Consecutivo"
                  hasFeedback
                >
                  <Select dataSource={consecutivos} />
                </Form.Item>
              </Col>
            )}
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
      </Spin>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        content={`Segmento en posición ${form.getFieldValue('posicion')}`}
        loading={loading}
      />
    </Row>
  );
};

SegmentosFoliador.propTypes = {
  idConfiguracion: PropTypes.string,
  isMutable: PropTypes.bool.isRequired,
  setSegmentos: PropTypes.func.isRequired,
};

SegmentosFoliador.defaultProps = {
  idConfiguracion: null,
};

export default SegmentosFoliador;
